import {
  CanChange,
  Coverage,
  InsuranceDetails,
  InsuranceDetailsView,
  Risk,
} from 'Api/insurance';
import { CoveragePair } from 'Components/CoverageSection/coverageSection.types';
import {
  BOAT_COVERAGES,
  CABIN_COVERAGES,
  CAR_COVERAGES,
  CARAVAN_COVERAGES,
  HOUSE_COVERAGES,
  MOTORCYCLE_COVERAGES,
} from 'Constants/coverageProduct.constants';
import { ProductName } from 'Constants/products.constants';
import {
  BoatCoverageProduct,
  CabinCoverageProduct,
  CaravanCoverageProduct,
  CarCoverage,
  CarCoverageProduct,
  HouseCoverage,
  HouseCoverageProduct,
  MotorcycleCoverageProduct,
  VehicleCoverage,
} from 'Types/coverages/coverages.types';
import { getConditionCodeFromCwp } from 'Utils/detailsCwp.utils';
import { formatMoney } from 'Utils/formatting/formatting.utils';
import { getAppText } from 'Utils/text';
import { isDefined } from 'Utils/type.utils';

/**
 * Some insurances created manually in F2100 may miss some of these properties,
 * this will fail when trying to change insurance.
 */
const isVehicleInsuranceWithMissingProperty = (
  insurance: InsuranceDetails,
): boolean => {
  if (insurance.insuranceProduct === ProductName.CAR) {
    const { mileage, mileageDate, maxMileage, maxMileageDate, coverage } =
      insurance;

    return (
      mileage == null ||
      !mileageDate ||
      maxMileage == null ||
      !maxMileageDate ||
      coverage === CarCoverage.INVALID_COVERAGE
    );
  }
  if (insurance.insuranceProduct === ProductName.MOTORCYCLE) {
    return insurance.coverage === VehicleCoverage.INVALID_COVERAGE;
  }

  return false;
};

export const getCoverages = (risks: Risk[]): Coverage[] => {
  return risks.flatMap((risk) => risk.coverages);
};

export const isAutoAndNotAllowedToChange = (
  insuranceDetails: InsuranceDetails,
) => isVehicleInsuranceWithMissingProperty(insuranceDetails);

export const isAkaCoInsuredAndNotAllowedToChange = (
  insuranceDetailsView: InsuranceDetailsView,
): boolean => insuranceDetailsView.canChange === CanChange.IS_COINSURED;

/**
 * NOTE: Temporary patch for - https://jira.storebrand.no/browse/CLI-1162
 */
export const isFireAndHouseNatureAndNotAllowedToChange = (
  coverageType: string,
  risks: Risk[],
): boolean => {
  if (!coverageType || risks.length === 0) {
    return false;
  }

  const coverages = getCoverages(risks);

  const isFireCoverage = coverageType.includes(HouseCoverage.FIRE);
  const isHouseNatureCoverageProduct = coverages.some(
    (coverage) =>
      coverage.coverageProduct === HouseCoverageProduct.HOUSE_NATURE,
  );

  return isFireCoverage && isHouseNatureCoverageProduct;
};

/**
 * Replace all occurrences matching {gValue: number} with the multiplied value
 * returns fallback 'check insurance document' text if it fails
 */
export function replaceGValues(
  values: Array<CoveragePair>,
  gValue: string | undefined,
): Array<CoveragePair> {
  const gValueRegEx = /{gValue: [-.0-9]+}/g;
  const numberRegEx = /(\d*[.])?\d+/g;
  const parsedGValue = Number(gValue);

  return values.map((translation) => ({
    ...translation,
    about:
      translation.about &&
      translation.about.map((text) => {
        // keep track of index shift to properly place multiple occurences
        let indexShift = 0;
        let newText = text;
        const placeholders = [...text.matchAll(gValueRegEx)];

        placeholders.forEach((placeholder) => {
          if (placeholder.index === undefined) {
            return;
          }
          let textReplacement = `(${getAppText(
            'detailed.secondary.coverage.fallback',
          )})`;
          const placeholderText = placeholder[0];
          const multiplierText = placeholderText.match(numberRegEx);
          const multiplier = multiplierText?.[0] && Number(multiplierText[0]);
          const placeholderStartIndex = placeholder.index + indexShift;
          const placeholderEndIndex =
            placeholderStartIndex + placeholderText.length;

          if (
            multiplier &&
            !Number.isNaN(multiplier) &&
            !Number.isNaN(parsedGValue)
          ) {
            textReplacement = formatMoney(
              Math.round(parsedGValue * multiplier),
            );
          }

          if (newText.length > placeholderEndIndex) {
            newText =
              newText.slice(0, placeholderStartIndex) +
              textReplacement +
              newText.slice(placeholderEndIndex);
            indexShift += textReplacement.length - placeholderText.length;
          }
        });
        return newText;
      }),
  }));
}

export const getOwnContributionValueFromRisk = (
  risks: Risk[],
): number | undefined => {
  const coverages = getCoverages(risks);

  const AFFECTED_COVERAGES = [
    ...BOAT_COVERAGES,
    ...CABIN_COVERAGES,
    ...CAR_COVERAGES,
    ...CARAVAN_COVERAGES,
    ...HOUSE_COVERAGES,
    ...MOTORCYCLE_COVERAGES,
  ];

  const getValueFromRisk = coverages.find((coverage) =>
    AFFECTED_COVERAGES.includes(
      coverage.coverageProduct as
        | BoatCoverageProduct
        | CaravanCoverageProduct
        | CarCoverageProduct
        | HouseCoverageProduct
        | CabinCoverageProduct
        | MotorcycleCoverageProduct,
    ),
  );

  return getValueFromRisk ? Number(getValueFromRisk.ownRisk.value) : undefined;
};

export const isPolicyUnderRenewalAndHasNewTerms = (
  insurance: InsuranceDetails,
): boolean => {
  const { conditionCodeToday, conditionCodeAfterChange } =
    getConditionCodeFromCwp(insurance);
  const { isInRenewal } = insurance.detailsCwp;
  return (
    isInRenewal &&
    isDefined(conditionCodeAfterChange) &&
    conditionCodeToday !== conditionCodeAfterChange
  );
};
